import { useState, useRef } from "react";
import { createPortal } from "react-dom";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { Lightbulb, X, Send, Bug, Sparkles, Paperclip, Image } from "lucide-react";

export function FeedbackWidget({ compact = false }: { compact?: boolean }) {
  const [isOpen, setIsOpen] = useState(false);
  const [type, setType] = useState<"idea" | "bug" | "improvement">("idea");
  const [message, setMessage] = useState("");
  const [attachedFile, setAttachedFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast({ title: "File too large", description: "Please select a file under 5MB.", variant: "destructive" });
      return;
    }

    const allowedTypes = ["image/png", "image/jpeg", "image/gif", "image/webp", "application/pdf"];
    if (!allowedTypes.includes(file.type)) {
      toast({ title: "Unsupported file type", description: "Please attach an image (PNG, JPG, GIF, WebP) or PDF.", variant: "destructive" });
      return;
    }

    setAttachedFile(file);

    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (ev) => setFilePreview(ev.target?.result as string);
      reader.readAsDataURL(file);
    } else {
      setFilePreview(null);
    }
  };

  const removeFile = () => {
    setAttachedFile(null);
    setFilePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const uploadFile = async (file: File): Promise<string> => {
    const urlRes = await fetch("/api/uploads/request-url", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        name: file.name,
        size: file.size,
        contentType: file.type,
      }),
    });

    if (!urlRes.ok) throw new Error("Failed to get upload URL");
    const { uploadURL, objectPath } = await urlRes.json();

    const uploadRes = await fetch(uploadURL, {
      method: "PUT",
      body: file,
      headers: { "Content-Type": file.type },
    });

    if (!uploadRes.ok) throw new Error("Failed to upload file");
    return objectPath;
  };

  const submitMutation = useMutation({
    mutationFn: async () => {
      setIsUploading(true);
      let attachmentUrl: string | undefined;

      if (attachedFile) {
        attachmentUrl = await uploadFile(attachedFile);
      }

      const page = window.location.pathname;
      const response = await apiRequest("POST", "/api/feedback", {
        type,
        page,
        message,
        attachmentUrl,
      });
      return response.json();
    },
    onSuccess: () => {
      toast({ title: "Thanks for your feedback!", description: "Your suggestion has been submitted." });
      setMessage("");
      setType("idea");
      setAttachedFile(null);
      setFilePreview(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      setIsUploading(false);
      setIsOpen(false);
    },
    onError: () => {
      setIsUploading(false);
      toast({ title: "Failed to submit feedback", variant: "destructive" });
    },
  });

  const typeIcons = {
    idea: <Lightbulb className="h-4 w-4" />,
    bug: <Bug className="h-4 w-4" />,
    improvement: <Sparkles className="h-4 w-4" />,
  };

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setIsOpen(!isOpen)}
        data-testid="button-feedback-toggle"
        title="Share feedback"
      >
        {isOpen ? <X className="h-5 w-5" /> : <Lightbulb className="h-5 w-5" />}
      </Button>

      {isOpen && createPortal(
        <div className="fixed top-14 right-4 z-[200] w-80 rounded-lg border text-card-foreground shadow-xl" style={{ backgroundColor: "hsl(var(--card))" }} data-testid="feedback-panel">
          <div className="flex items-start justify-between p-4 border-b">
            <div>
              <h3 className="font-semibold text-sm">Share Your Feedback</h3>
              <p className="text-xs text-muted-foreground mt-1">
                Got an idea, found a bug, or want something improved?
              </p>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="ml-2 -mt-0.5 h-6 w-6 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors flex-shrink-0"
              data-testid="button-feedback-close"
              aria-label="Close feedback"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
          <div className="p-4 space-y-3">
            <div>
              <Select value={type} onValueChange={(v: "idea" | "bug" | "improvement") => setType(v)}>
                <SelectTrigger className="h-9" data-testid="select-feedback-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-[300]">
                  <SelectItem value="idea">
                    <span className="flex items-center gap-2">
                      {typeIcons.idea}
                      Idea
                    </span>
                  </SelectItem>
                  <SelectItem value="bug">
                    <span className="flex items-center gap-2">
                      {typeIcons.bug}
                      Bug Report
                    </span>
                  </SelectItem>
                  <SelectItem value="improvement">
                    <span className="flex items-center gap-2">
                      {typeIcons.improvement}
                      Improvement
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Describe your idea or what you'd like to see..."
              className="resize-none text-sm"
              rows={3}
              data-testid="input-feedback-message"
            />

            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/gif,image/webp,application/pdf"
              onChange={handleFileSelect}
              className="hidden"
              data-testid="input-feedback-file"
            />

            {attachedFile ? (
              <div className="flex items-center gap-2 rounded-md border p-2 text-xs" data-testid="feedback-file-preview">
                {filePreview ? (
                  <img src={filePreview} alt="Preview" className="h-10 w-10 rounded object-cover flex-shrink-0" />
                ) : (
                  <div className="h-10 w-10 rounded bg-muted flex items-center justify-center flex-shrink-0">
                    <Paperclip className="h-4 w-4 text-muted-foreground" />
                  </div>
                )}
                <span className="truncate flex-1 text-muted-foreground">{attachedFile.name}</span>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={removeFile}
                  data-testid="button-remove-attachment"
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => fileInputRef.current?.click()}
                data-testid="button-attach-file"
              >
                <Image className="h-3 w-3 mr-2" />
                Attach Screenshot
              </Button>
            )}

            <div className="flex justify-end">
              <Button
                size="sm"
                onClick={() => submitMutation.mutate()}
                disabled={!message.trim() || submitMutation.isPending || isUploading}
                data-testid="button-submit-feedback"
              >
                <Send className="h-3 w-3 mr-2" />
                {submitMutation.isPending ? "Sending..." : "Send"}
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
